import { useState, useContext, MutableRefObject } from 'react';

import { CSSObject } from 'styled-components';

import theme, { StylableComponent } from '@adsk/alloy-react-theme';

import InlineEditContext from './InlineEditContext';
import { ValueType } from './types';

type UseInlineInputProps<TElement, TValue> = {
  inputRef: MutableRefObject<TElement>;
  value: TValue;
  error?: boolean;
  isTextArea?: boolean;
  saveUnmodifiedValue?: boolean;
  cancelOnBlur?: boolean;
};

export default <
  TElement extends HTMLInputElement | HTMLTextAreaElement,
  TValue extends ValueType,
>({
  inputRef,
  value,
  isTextArea = false,
  saveUnmodifiedValue = false,
  cancelOnBlur = false,
  error = false,
}: UseInlineInputProps<TElement, TValue>) => {
  const [fieldValue, setFieldValue] = useState<TValue>(value);
  const [modified, setModified] = useState<boolean>(saveUnmodifiedValue);
  const inlineEdit = useContext(InlineEditContext);

  const handleSubmit = () => {
    if (!error) {
      setModified(false);
      inlineEdit.updateValue?.(fieldValue);
    }
  };
  const handleCancel = () => {
    setModified(false);
    setFieldValue(value);
    inlineEdit.setEditMode?.(false);
    inlineEdit.onEditExit?.(value, { canceled: true });

    // setFieldValue doesn't cause onChange to fire, so let's trigger it
    // https://stackoverflow.com/a/46012210/2883129
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window[isTextArea ? 'HTMLTextAreaElement' : 'HTMLInputElement'].prototype,
      'value',
    )?.set;
    nativeInputValueSetter?.call(inputRef.current, value);

    const event = new Event('input', { bubbles: true });
    inputRef.current?.dispatchEvent(event);
  };

  const onChange: React.ChangeEventHandler<TElement> = (e) => {
    setFieldValue(e.target.value as TValue);
    setModified(e.target.value !== value);
    inlineEdit.inputOnChange?.(e);
  };
  const onKeyDown: React.KeyboardEventHandler<TElement> = (e) => {
    // Japanese uses an IME (input method editor) to select characters
    // Let's prevent submission when user is selecting from the IME
    // https://lists.w3.org/Archives/Public/www-dom/2010JulSep/att-0182/keyCode-spec.html
    if (e.key === 'Enter' && e.keyCode !== 229) {
      handleSubmit();
      e.preventDefault();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return {
    getWrapperProps: ({
      style = {},
      ...props
    }: StylableComponent<HTMLElement>) => ({
      onBlur: cancelOnBlur ? handleCancel : handleSubmit,
      style: {
        ...theme.typography.labelMedium,
        alignItems: 'center',
        display: 'flex',
        margin: `${theme.spacing.xxSmall}px ${theme.spacing.xSmall}px ${theme.spacing.xxSmall}px ${theme.spacing.small}px`,
        width: '100%',
        ...(style as CSSObject),
      } as CSSObject,
      ...props,
    }),
    getInputProps: ({ style = {}, ...props }: StylableComponent<TElement>) => ({
      value: fieldValue,
      ref: inputRef,
      autoFocus: true,
      dir: 'auto',
      style: {
        // Allow consumer to set custom typography
        fontFamily: 'inherit',
        fontWeight: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        // Base styling
        background: theme.colors.white,
        border: 'none',
        color: theme.colors.charcoal900,
        marginLeft: `-${theme.spacing.xxxSmall}px`,
        padding: 0,
        outline: 'none',
        width: '100%',
        boxSizing: 'content-box',
        '&::placeholder': {
          color: theme.colors.charcoal500,
        },
        ...(style as CSSObject),
      } as CSSObject,
      onChange,
      onKeyDown,
      ...props,
    }),
    modified,
    handleSubmit,
    handleCancel,
    onChange,
    onKeyDown,
  };
};
